<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("msg.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main>
        <div class="mx-auto px-12 py-10">
          <div class="flex items-start justify-between">
            <div class="flex flex-col space-y-3">
              <div class="flex items-center gap-3">
                <h1
                  tabindex="-1"
                  class="no-focus-outline scroll-m-20 text-nowrap text-3xl font-bold tracking-tight"
                  ref="initialFocus"
                >
                  {{ t("pages.apiKeys.heading.title") }}
                </h1>
                <a
                  class="ml-2 inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  href="https://docs.knocknoc.io/books/admin-guide/chapter/knocknoc-api"
                  target="_blank"
                  :aria-label="`${t('pages.apiKeys.docs')} (${t('common.opensInNewTab')})`"
                >
                  {{ t("common.docs") }}
                  <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                  <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
                </a>
              </div>

              <div class="max-w-2xl">
                <p class="text-base leading-relaxed text-muted-foreground">
                  {{ t("pages.apiKeys.heading.description") }}
                </p>
              </div>
            </div>

            <div class="flex-shrink-0">
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm ring-offset-background transition-all duration-200 hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="
                  closeModal();
                  openInstanceModal();
                "
              >
                {{ t("pages.apiKeys.heading.actions.create") }}
              </button>
            </div>
          </div>

          <div class="mt-8 flow-root min-h-screen overflow-x-auto">
            <AutoTable :columns="apiKeyTableCols" :rows="apiKeyTableRows">
              <template #default>
                <TableRow>
                  <TableCell :colspan="apiKeyTableCols?.length" class="text-center text-muted-foreground">
                    <div class="flex items-center justify-center gap-2">
                      <InformationCircleIcon class="h-4 w-4" aria-hidden="true" />
                      <span>
                        {{ t("common.getStarted", { button: t("msg.createApiKey") }) }}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              </template>

              <template #col-Edit="{ data }">
                <Menu as="div" class="inline-block">
                  <MenuButton
                    class="inline-flex w-full justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
                  >
                    {{ t("msg.actions") }}
                    <ChevronDownIcon
                      class="-mr-1 ml-2 h-5 w-5 text-violet-200 hover:text-violet-100"
                      aria-hidden="true"
                    />
                  </MenuButton>

                  <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                  >
                    <MenuItems
                      anchor="bottom end"
                      class="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-[#e05454] rounded-md bg-background shadow-lg ring-1 ring-[#e05454] focus:outline-none"
                    >
                      <div class="px-1 py-1">
                        <MenuItem>
                          <button
                            @click="
                              currentApiKey = data;
                              openInstanceModal();
                            "
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm text-white"
                          >
                            <PencilSquareIcon class="mr-2 h-5 w-5 text-[#e05454]" aria-hidden="true" />
                            {{ t("msg.edit") }}
                          </button>
                        </MenuItem>
                      </div>
                      <div class="px-1 py-1">
                        <MenuItem>
                          <button
                            @click="
                              currentApiKey = data;
                              deleteModal = true;
                            "
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm text-white"
                            aria-label="Delete api key"
                          >
                            <TrashIcon class="mr-2 h-5 w-5 text-[#e05454]" aria-hidden="true" />
                            {{ t("msg.delete") }}
                          </button>
                        </MenuItem>
                      </div>
                    </MenuItems>
                  </transition>
                </Menu>
              </template>
            </AutoTable>
          </div>
        </div>
      </main>
    </div>
  </div>

  <Dialog :show="deleteModal" v-if="currentApiKey">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ t("pages.apiKeys.dialogs.delete.header") }}
      </DialogTitle>

      <DialogDescription>
        {{ t("msg.confirmDelete", { name: currentApiKey.Name }) }}
      </DialogDescription>

      <div class="flex space-x-3">
        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
          @click="closeModal"
          ref="cancelButtonRef"
        >
          {{ t("msg.cancel") }}
        </button>

        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e05454] sm:col-start-2"
          @click="deleteApiKey()"
        >
          {{ t("msg.delete") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="createModal">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{
          currentApiKey == null
            ? t("pages.apiKeys.dialogs.create.headerCreate")
            : t("pages.apiKeys.dialogs.create.headerUpdate")
        }}
      </DialogTitle>

      <FormKit
        type="form"
        :actions="false"
        @submit="saveApiKey"
        @submit-invalid="globals.focusFirstError"
        :value="currentApiKey"
      >
        <div class="flex flex-col space-y-4">
          <div>
            <FormKit
              type="text"
              name="Name"
              :disabled="currentApiKey !== null"
              :classes="{ outer: { 'max-w-[20em]': false } }"
              :label="t('fields.apiKeys.name')"
              validation="required"
              :autocomplete="autoComplete"
            />

            <FormKit
              v-if="currentApiKey === null"
              type="select"
              name="ExpiredAfter"
              :label="t('Expires')"
              :options="{
                '12months': t('in 12 months'),
                '24months': t('in 24 months'),
                never: t('never'),
              }"
              validation="required"
            />

            <FormKit
              type="select"
              v-model="apiKeyPurpose"
              :disabled="currentApiKey !== null"
              name="Purpose"
              :label="t('Purpose')"
              :options="{
                allowlists: t('Read Allowlists'),
                agent_register: t('Register Agents'),
                user_sessions: t('Manage User Sessions'),
                user_auth: t('User Authentication'),
              }"
            />

            <FormKit
              v-if="apiKeyPurpose === 'allowlists'"
              type="select"
              multiple
              :disabled="currentApiKey !== null"
              name="Allowlists"
              :label="t('fields.apiKeys.allowlist')"
              :options="allowlists"
              validation="required"
            />

            <FormKit
              v-if="apiKeyPurpose === 'user_auth'"
              type="select"
              :disabled="currentApiKey !== null"
              name="AuthUserId"
              :label="t('User')"
              :options="users.map((u) => ({ label: u.Name, value: u.ID }))"
              validation="required"
            />

            <label
              class="formkit-label mb-1 inline-flex text-sm font-bold text-neutral-700 dark:text-neutral-300"
              for="allowedFromNewPrefix"
            >
              {{ t("fields.apiKeys.allowedFrom") }}
            </label>
            <div class="grid grid-cols-3">
              <template v-for="(allowedFromEntry, index) in allowedFrom">
                <span class="col-span-2">
                  {{ allowedFromEntry.prefix }}
                  {{ allowedFromEntry.note ? `(${allowedFromEntry.note})` : "" }}
                </span>
                <button type="button" class="p-1" @click="removeAllowedFrom(index)" :title="t('msg.remove')">
                  <MinusCircleIcon class="inline h-5 w-5 text-[#e05454]" />
                </button>
              </template>
              <div class="relative mr-1">
                <input
                  class="peer block border-0 bg-background py-1.5 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
                  type="text"
                  v-model="newAllowedFromPrefix"
                  placeholder="192.0.2.0/24"
                  id="allowedFromNewPrefix"
                  :autocomplete="autoComplete"
                />
                <div
                  class="absolute inset-x-0 bottom-0 border-t border-[#ccd1c4] peer-focus:border-t-2 peer-focus:border-[#e05454]"
                  aria-hidden="true"
                ></div>
              </div>
              <div class="relative">
                <input
                  class="peer block border-0 bg-background py-1.5 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
                  type="text"
                  v-model="newAllowedFromNote"
                  :placeholder="randomNotePlaceholder"
                  id="allowedFromNewNote"
                  :autocomplete="autoComplete"
                />
                <div
                  class="absolute inset-x-0 bottom-0 border-t border-[#ccd1c4] peer-focus:border-t-2 peer-focus:border-[#e05454]"
                  aria-hidden="true"
                ></div>
              </div>
              <button type="button" class="p-1" @click="addAllowedFrom" :title="t('msg.add')">
                <PlusCircleIcon class="inline h-5 w-5 text-[#e05454]" />
              </button>
            </div>
            <p v-if="newAllowedFromError" id="newAllowedFromError" class="text-xs text-[#e05454]">
              {{ newAllowedFromError }}
            </p>
          </div>

          <div class="flex space-x-3">
            <button
              type="button"
              class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
              @click="closeModal"
              ref="cancelButtonRef"
            >
              {{ t("msg.cancel") }}
            </button>

            <button
              type="submit"
              class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e05454] sm:col-start-2"
            >
              {{ currentApiKey == null ? t("msg.create") : t("msg.update") }}
            </button>
          </div>
        </div>
      </FormKit>
    </DialogPanel>
  </Dialog>

  <Dialog :show="showNewModal" v-if="currentApiKey">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ t("msg.newApiKeyCreated") }}
      </DialogTitle>

      <div class="ml-1 inline-flex" v-if="apiKeyPurpose === 'user_sessions'">
        <a
          class="inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          href="https://docs.knocknoc.io/books/admin-guide/page/manage-user-sessions"
          target="_blank"
          :aria-label="`${t('pages.apiKeys.dialogs.create.userSessionDocs')} (${t('common.opensInNewTab')})`"
        >
          {{ t("common.docs") }}
          <ArrowTopRightOnSquareIcon class="h-3 w-3" />
        </a>
      </div>
      <DialogDescription class="flex flex-col space-y-1">
        <span>{{ t("msg.copyYourApiKey") }}</span>
        <CopySpan
          v-if="apiKeyPurpose === 'user_sessions'"
          :display="`URL: ${userSessionsApiEndpoint}`"
          :value="userSessionsApiEndpoint"
        />
        <CopySpan :display="`API Key: ${currentApiKey.PlaintextKey}`" :value="currentApiKey.PlaintextKey" />
      </DialogDescription>

      <button
        type="button"
        class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
        @click="closeModal"
        ref="cancelButtonRef"
      >
        {{ t("common.close") }}
      </button>
    </DialogPanel>
  </Dialog>

  <ErrorNotification :error="errorMessage" :title="t('msg.error')" @update:error="(input) => (errorMessage = input)" />

  <InfoNotification :text="infoMessage" @update:info="(input) => (infoMessage = input)" />
</template>

<script setup>
import { FormKit } from "@formkit/vue";
import { DialogDescription, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  ChevronDownIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  TrashIcon,
} from "@heroicons/vue/20/solid";

import { DateTime } from "luxon";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import globals from "../../globals";

import CopySpan from "../../components/CopySpan.vue";
import SideBar from "../../components/SideBar.vue";
import SearchBar from "../../components/SearchBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";
import InfoNotification from "../../components/InfoNotification.vue";
import userService from "../../services/user.service.ts";
import Dialog from "../../components/ui/dialog/Dialog.vue";
import DialogOverlay from "../../components/ui/dialog/DialogOverlay.vue";
import DialogPanel from "../../components/ui/dialog/DialogPanel.vue";
import DialogTitle from "../../components/ui/dialog/DialogTitle.vue";
import AutoTable from "../../components/ui/table/AutoTable.vue";
import AdminHeader from "../../components/AdminHeader.vue";
import { useUserStore } from "../../pinia/user.ts";
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import TableRow from "../../components/ui/table/TableRow.vue";
import TableCell from "../../components/ui/table/TableCell.vue";

const { t } = useI18n();

const userStore = useUserStore();

const initialFocus = ref();

const errorMessage = ref("");
const infoMessage = ref("");

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

const userSessionsApiEndpoint = computed(() => {
  return `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ""}/api/v1/user_sessions`;
});

const search = ref("");
const sidebarOpen = ref(false);
const createModal = ref(false);
const showNewModal = ref(false);
const deleteModal = ref(false);

const currentApiKey = ref(null);
const apiKeys = ref([]);
const allowlists = ref({});
const users = ref({});
const apiKeyPurpose = ref("allowlists");

const apiKeyTableCols = [
  { key: "Name", label: t("fields.apiKeys.name"), sortable: true },
  { key: "AllowsAccessTo", label: t("fields.apiKeys.allowsAccessTo") },
  { key: "CreatedAt", label: t("fields.apiKeys.age"), sortable: true },
  { key: "ExpiredAfter", label: t("Expires"), sortable: true },
  { key: "LastUsedAt", label: t("Last Used"), sortable: true },
  { key: "AllowedFrom", label: t("fields.apiKeys.allowedFrom") },
  { key: "Edit", label: t("msg.edit"), hidden: true },
];

const apiKeyTableRows = computed(() =>
  filterData.value.map((data) => ({
    Name: data.Name,
    AllowsAccessTo: data.Resources.join(", "),
    CreatedAt: DateTime.fromISO(data.CreatedAt).toRelative(),
    ExpiredAfter: data.ExpiredAfter ? DateTime.fromISO(data.ExpiredAfter).toRelative() : t("Never"),
    LastUsedAt: data.LastUsedAt ? DateTime.fromISO(data.LastUsedAt).toRelative() : t("Never"),
    AllowedFrom: data.AllowedFrom?.map((e) => {
      let note = e.note ? ` (${e.note})` : "";
      return `${e.prefix}${note}`;
    }).join(", "),
    Edit: data,
  })),
);

const closeModal = () => {
  createModal.value = false;
  showNewModal.value = false;
  deleteModal.value = false;

  apiKeyPurpose.value = "allowlists";
  currentApiKey.value = null;
};

const openInstanceModal = () => {
  createModal.value = true;

  if (currentApiKey.value === null) {
    allowedFrom.value = [
      { prefix: "0.0.0.0/0", note: "Allow entire Internet IPv4" },
      { prefix: "::/0", note: "Allow entire Internet IPv6" },
    ];
  } else {
    allowedFrom.value = currentApiKey.value.AllowedFrom;
    if (currentApiKey.value.Scopes[0] === "agent.register") {
      apiKeyPurpose.value = "agent_register";
    } else if (currentApiKey.value.Scopes[0] === "user.sessions") {
      apiKeyPurpose.value = "user_sessions";
    } else if (/^allowlist/.test(currentApiKey.value.Scopes[0])) {
      apiKeyPurpose.value = "allowlists";
      currentApiKey.value.Allowlists = currentApiKey.value.Scopes.map((s) => s.replace(/^allowlist.read:/, ""));
    } else if (/^user\.auth:/.test(currentApiKey.value.Scopes[0])) {
      currentApiKey.value.AuthUserId = currentApiKey.value.Scopes[0].replace(/^user.auth:/, "");
      apiKeyPurpose.value = "user_auth";
    }
  }
};

const allowedFrom = ref([]);
const newAllowedFromPrefix = ref("");
const newAllowedFromNote = ref(null);
const newAllowedFromError = ref("");

const addAllowedFrom = () => {
  if (validCidr(newAllowedFromPrefix.value)) {
    allowedFrom.value.push({ prefix: newAllowedFromPrefix.value, note: newAllowedFromNote.value });
    newAllowedFromPrefix.value = "";
    newAllowedFromNote.value = null;
    newAllowedFromError.value = "";
  } else {
    newAllowedFromError.value = t("err.invalidCidr");
  }
};

const notePlaceholders = [
  "Mallory's hoodie",
  "Alice's house",
  "Bruce's office",
  "Charlie's cafe",
  "Darla's datacenter",
];
const randomNotePlaceholder = ref(notePlaceholders[Math.floor(Math.random() * notePlaceholders.length)]);

const validCidr = (cidr) => {
  const octet = "(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])";
  const sextet = "[0-9a-fA-F]{1,4}";
  const v4cidr = "/(3[0-2]|[21][0-9]|[0-9])";
  const ipv4 = `(${octet}\\.){3}${octet}${v4cidr}`;
  const v6cidr = "/(12[0-8]|1[01][0-9]|[1-9][0-9]|[0-9])";
  const ipv6 = `
	  (
	   (${sextet}:){7}${sextet}|
	   (${sextet}:){1,7}:|
	   (${sextet}:){1,6}:${sextet}|
	   (${sextet}:){1,5}(:${sextet}){1,2}|
	   (${sextet}:){1,4}(:${sextet}){1,3}|
	   (${sextet}:){1,3}(:${sextet}){1,4}|
	   (${sextet}:){1,2}(:${sextet}){1,5}|
	   ${sextet}:((:${sextet}){1,6})|
	   :(((:${sextet}){1,7})|:)|
	   fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|
	   ::(ffff(:0{1,4}){0,1}:){0,1}${ipv4}|
	   (${sextet}:){1,4}:${ipv4}
	  )${v6cidr}`;
  const ipv46Regex = new RegExp(`^(${ipv4}|${ipv6})$`.replace(/\s+/g, ""));

  return ipv46Regex.test(cidr);
};

const removeAllowedFrom = (index) => {
  allowedFrom.value.splice(index, 1);
};

const filterData = computed(() => {
  if (!apiKeys.value) return [];
  const searchTerm = search.value?.toString().toLocaleLowerCase() ?? "";

  return apiKeys.value.filter((item) => {
    if (!searchTerm) return true;
    return Object.values(item).some((val) => val?.toString().toLocaleLowerCase().includes(searchTerm) ?? false);
  });
});

const deleteApiKey = () => {
  if (currentApiKey.value !== null) {
    userService.deleteApiKey(currentApiKey.value.ID).then(
      () => {
        fetchData();
        infoMessage.value = t("msg.apiKeyDeleted");
      },
      (error) => (errorMessage.value = globals.renderError(t, error)),
    );
  }
  deleteModal.value = false;
  currentApiKey.value = null;
};

const saveApiKey = (fields) => {
  let data = {
    ID: currentApiKey.value?.ID,
    Name: fields.Name,
    AllowedFrom: allowedFrom.value,
  };

  if (fields.ExpiredAfter === "12months") {
    data.ExpiredAfter = new Date();
    data.ExpiredAfter.setFullYear(data.ExpiredAfter.getFullYear() + 1);
  } else if (fields.ExpiredAfter === "24months") {
    data.ExpiredAfter = new Date();
    data.ExpiredAfter.setFullYear(data.ExpiredAfter.getFullYear() + 2);
  }

  if (fields.Purpose === "allowlists") {
    data.Scopes = fields.Allowlists.map((id) => `allowlist.read:${id}`);
  } else if (fields.Purpose === "agent_register") {
    data.Scopes = ["agent.register"];
  } else if (fields.Purpose === "user_sessions") {
    data.Scopes = ["user.sessions"];
  } else if (fields.Purpose === "user_auth") {
    data.Scopes = [`user.auth:${fields.AuthUserId}`];
  }

  if (currentApiKey.value === null) {
    userService.createApiKey(JSON.stringify(data)).then(
      (response) => {
        currentApiKey.value = response.data;
        showNewModal.value = true;
        createModal.value = false;
        fetchData();
      },
      (error) => {
        createModal.value = false;
        fetchData();
        errorMessage.value = globals.renderError(t, error);
      },
    );
  } else {
    userService.updateApiKey(currentApiKey.value.ID, JSON.stringify(data)).then(
      (response) => {
        createModal.value = false;
        infoMessage.value = t("API Key updated");
        fetchData();
      },
      (error) => {
        createModal.value = false;
        fetchData();
        errorMessage.value = globals.renderError(t, error);
      },
    );
  }
};

const fetchData = () => {
  userStore.fetchApiKeys({ force: true }).then(
    (response) => {
      apiKeys.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
};

const fetchAllowlists = () => {
  userStore.fetchAcls({ force: true }).then(
    (response) => {
      var newAllowlists = {};

      response.data.forEach((acl, _) => {
        if (Object.keys(acl.BackendConfig)[0] == "allowlist") {
          newAllowlists[acl.ID] = acl.Description;
        }
      });

      allowlists.value = newAllowlists;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
};

const fetchUsers = () => {
  userStore.fetchUsers().then(
    (response) => {
      users.value = response.data
        .filter((u) => u.AuthType === "apikey")
        .map((u) => ({
          ID: u.ID,
          Name: u.Name,
        }))
        .sort(globals.smartSortFunc(globals.sortObjBy("Name")));
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
};

watch(initialFocus, async (el) => {
  if (el) {
    await nextTick();
    el.focus();
  }
});

onMounted(() => {
  fetchData();
  fetchAllowlists();
  fetchUsers();
});
</script>
